import { create } from 'zustand';
import type {
  TranslationDirection,
  ConnectionStatus,
  CaptionState,
  TranscriptEntry,
  OBSStyleMode,
} from '../types';
import { TARGET_LABELS } from '../types';

// ==============================
// デフォルト用語集
// ==============================
const DEFAULT_GLOSSARY: string[] = [];

// ==============================
// ストア型定義
// ==============================
interface SessionState {
  // 接続
  connectionStatus: ConnectionStatus;
  errorMessage: string | null;
  reconnectInfo: { attempt: number; delay: number } | null;

  // 翻訳
  direction: TranslationDirection;
  paused: boolean;
  isSpeaking: boolean;

  // 字幕
  currentCaption: CaptionState | null;
  currentCaption2: CaptionState | null; // 2つ目の言語（同時表示）

  // 設定
  fontSize: number;
  showOriginal: boolean;
  obsStyle: OBSStyleMode;
  captionPosition: 'bottom' | 'top' | 'center';
  sessionName: string;

  // 用語集
  glossaryTerms: string[];

  // 履歴
  transcript: TranscriptEntry[];
  transcriptDirty: boolean; // 最後に保存（Export）してから記録が増えたか

  // タイマー
  startTime: number | null;

  // アクション
  setConnectionStatus: (s: ConnectionStatus, error?: string) => void;
  setReconnectInfo: (info: { attempt: number; delay: number } | null) => void;
  setDirection: (d: TranslationDirection) => void;
  setPaused: (v: boolean) => void;
  setIsSpeaking: (v: boolean) => void;
  setCurrentCaption: (c: CaptionState | null) => void;
  setCurrentCaption2: (c: CaptionState | null) => void;
  clearCaption: () => void;
  setFontSize: (n: number) => void;
  setShowOriginal: (v: boolean) => void;
  setOBSStyle: (s: OBSStyleMode) => void;
  setCaptionPosition: (p: 'bottom' | 'top' | 'center') => void;
  setSessionName: (n: string) => void;
  initFromEvent: (glossary: string[]) => void;
  setGlossaryTerms: (terms: string[]) => void;
  addGlossaryTerm: (term: string) => void;
  removeGlossaryTerm: (term: string) => void;
  addTranscriptEntry: (entry: Omit<TranscriptEntry, 'id'>) => void;
  appendTranscriptText: (id: string, text: string) => void;
  clearTranscript: () => void;
  exportTranscript: (format: 'txt' | 'csv' | 'json') => void;
  setStartTime: (t: number | null) => void;
  importGlossary: (terms: string[]) => void;
  exportGlossary: () => void;
}

// 記録のテキスト形式（古い順）。2言語モードの原文は「原文」、訳は「→ 日本語」などと表示する
export function transcriptToText(transcript: TranscriptEntry[]): string {
  return transcript
    .slice()
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((e) => {
      const label = e.target === 'src' ? '原文' : e.target ? `訳 → ${TARGET_LABELS[e.target] ?? e.target}` : e.direction;
      return `[${new Date(e.timestamp).toLocaleTimeString('ja-JP')}] ${label}\n` +
        (e.original ? `原文: ${e.original}\n` : '') +
        `${e.target === 'src' ? '' : e.original ? '訳文: ' : ''}${e.translation}\n`;
    })
    .join('\n');
}

export const useSessionStore = create<SessionState>((set, get) => ({
  // 初期値
  connectionStatus: 'idle',
  errorMessage: null,
  reconnectInfo: null,
  direction: 'en-ja',
  paused: false,
  isSpeaking: false,
  currentCaption: null,
  currentCaption2: null,
  fontSize: 54,
  showOriginal: false,
  obsStyle: 'transparent-white',
  captionPosition: 'top',
  sessionName: '',
  glossaryTerms: [...DEFAULT_GLOSSARY],
  transcript: [],
  transcriptDirty: false,
  startTime: null,

  // ─────────────────────────────
  // アクション
  // ─────────────────────────────

  setConnectionStatus: (connectionStatus, error) =>
    set({ connectionStatus, errorMessage: error ?? null }),

  setReconnectInfo: (reconnectInfo) => set({ reconnectInfo }),

  setDirection: (direction) => set({ direction }),

  setPaused: (paused) => set({ paused }),

  setIsSpeaking: (isSpeaking) => set({ isSpeaking }),

  setCurrentCaption: (currentCaption) => set({ currentCaption }),
  setCurrentCaption2: (currentCaption2) => set({ currentCaption2 }),

  clearCaption: () => set({ currentCaption: null, currentCaption2: null }),

  setFontSize: (fontSize) => set({ fontSize: Math.max(40, Math.min(96, fontSize)) }),

  setShowOriginal: (showOriginal) => set({ showOriginal }),

  setOBSStyle: (obsStyle) => set({ obsStyle }),

  setCaptionPosition: (captionPosition) => set({ captionPosition }),

  setSessionName: (sessionName) => set({ sessionName }),

  setGlossaryTerms: (glossaryTerms) => set({ glossaryTerms }),

  // イベントの用語集で初期化（OperatorPage マウント時に呼ぶ）
  initFromEvent: (glossary: string[]) => {
    set({ glossaryTerms: glossary.length > 0 ? glossary : [...DEFAULT_GLOSSARY] });
  },

  addGlossaryTerm: (term) => {
    const { glossaryTerms } = get();
    const trimmed = term.trim();
    if (trimmed && !glossaryTerms.includes(trimmed)) {
      set({ glossaryTerms: [...glossaryTerms, trimmed] });
    }
  },

  removeGlossaryTerm: (term) =>
    set((state) => ({
      glossaryTerms: state.glossaryTerms.filter((t) => t !== term),
    })),

  addTranscriptEntry: (entry) =>
    set((state) => ({
      // 最大500件に制限
      transcript: [
        ...state.transcript.slice(-499),
        { ...entry, id: crypto.randomUUID() },
      ],
      transcriptDirty: true,
    })),

  // 既存の記録（原文）に文字を足す（数秒ごとの文字起こしの切れ端を1件にまとめるため）
  appendTranscriptText: (id, text) =>
    set((state) => ({
      transcript: state.transcript.map((e) => (e.id === id ? { ...e, translation: e.translation + text } : e)),
      transcriptDirty: true,
    })),

  clearTranscript: () => set({ transcript: [], transcriptDirty: false }),

  exportTranscript: (format) => {
    const { transcript, sessionName } = get();
    if (transcript.length === 0) {
      alert('翻訳履歴がありません');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `transcript-${sessionName || 'session'}-${timestamp}`;
    let content = '';

    if (format === 'json') {
      content = JSON.stringify(transcript, null, 2);
    } else if (format === 'csv') {
      const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
      content =
        'Time,Direction,Original,Translation\n' +
        transcript
          .map(
            (e) =>
              `${escape(new Date(e.timestamp).toLocaleTimeString('ja-JP'))},` +
              `${escape(e.target ? '→' + (TARGET_LABELS[e.target] ?? e.target) : e.direction)},` +
              `${escape(e.original)},` +
              `${escape(e.translation)}`
          )
          .join('\n');
    } else {
      content = transcriptToText(transcript);
    }

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    set({ transcriptDirty: false });
  },

  setStartTime: (startTime) => set({ startTime }),

  importGlossary: (terms) => {
    const unique = [...new Set(terms.map((t) => t.trim()).filter(Boolean))];
    set({ glossaryTerms: unique });
  },

  exportGlossary: () => {
    const { glossaryTerms } = get();
    const content = JSON.stringify({ terms: glossaryTerms }, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'glossary.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
}));
